import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Plus, Search, MoreVertical, Calendar, DollarSign, Clock, TrendingUp, RefreshCw, Printer, FileDown } from 'lucide-react';
import { StatsCard, Modal } from '../ui';
import Button from '../ui/Button';
import apiService from '../../services/api';
import type { AdminCompanyRecord } from '../../services/api';
import './InvoicePage.css';

interface Invoice {
  id: string;
  invoiceId: string;
  companyId: string;
  company: {
    legalName: string;
  };
  period: string;
  amount: number | string;
  transactionCount: number;
  dueDate: string;
  status: 'PENDING' | 'PAID' | 'OVERDUE';
  createdAt: string;
}

interface BillingStats {
  monthlyRevenue: number;
  outstanding: number;
  paidThisMonth: number;
  avgPaymentTimeDays: number;
}

const InvoiceActionDropdown: React.FC<{ invoiceId: string; status: string }> = ({ invoiceId, status }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleAction = (action: string) => {
    console.log(`${action} for invoice ${invoiceId}`);
    setIsOpen(false);
  };

  return (
    <div className="invoice-action-dropdown">
      <button className="action-trigger" onClick={() => setIsOpen(!isOpen)} aria-label="More actions">
        <MoreVertical size={16} />
      </button>

      {isOpen && (
        <>
          <div className="dropdown-overlay" onClick={() => setIsOpen(false)} />
          <div className="dropdown-menu">
            <button className="dropdown-item" onClick={() => handleAction('download')}>
              <Download size={14} /> Download
            </button>
            {status === 'PENDING' && (
              <button className="dropdown-item" onClick={() => handleAction('send-reminder')}>
                <FileText size={14} /> Send Reminder
              </button>
            )}
            <button className="dropdown-item" onClick={() => handleAction('process-payment')}>
              <DollarSign size={14} /> Process Payment
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const InvoicePage: React.FC = () => {
  // State for data
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [companies, setCompanies] = useState<AdminCompanyRecord[]>([]);

  // State for UI
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // State for filters & pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [companyFilter, setCompanyFilter] = useState('All');
  const [periodFilter, setPeriodFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  // Form state for generation
  const [genPeriod, setGenPeriod] = useState('');
  const [genStartDate, setGenStartDate] = useState('');
  const [genEndDate, setGenEndDate] = useState('');

  const formatCurrency = (amount: number | string) => {
    const val = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const fetchStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const data = await apiService.getBillingDashboard();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch billing stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const query: any = {
        page,
        limit,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        companyId: companyFilter !== 'All' ? companyFilter : undefined,
        period: periodFilter || undefined
      };
      const response = await apiService.getInvoices(query);
      setInvoices(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, companyFilter, periodFilter]);

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await apiService.getAdminCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchCompanies();
  }, [fetchStats, fetchCompanies]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleGenerateInvoices = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsGenerating(true);
      await apiService.generateInvoices({
        period: genPeriod,
        startDate: genStartDate,
        endDate: genEndDate
      });
      setIsModalOpen(false);
      fetchInvoices();
      fetchStats();
      // Reset form
      setGenPeriod('');
      setGenStartDate('');
      setGenEndDate('');
    } catch (error) {
      alert('Failed to generate invoices. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRefresh = () => {
    fetchInvoices();
    fetchStats();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    if (invoices.length === 0) return;

    // Define headers
    const headers = ['Invoice ID', 'Company', 'Period', 'Amount', 'Transaction Count', 'Due Date', 'Status'];

    // Map data to CSV rows
    const rows = invoices.map(inv => [
      `"${inv.invoiceId}"`,
      `"${inv.company?.legalName || 'N/A'}"`,
      `"${inv.period}"`,
      inv.amount,
      inv.transactionCount,
      `"${new Date(inv.dueDate).toLocaleDateString()}"`,
      `"${inv.status}"`
    ]);

    // Create CSV string
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `invoices_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="invoice-page">
      <div className="invoice-header">
        <div className="invoice-title">
          <FileText size={24} />
          <h1>Billing & Invoices</h1>
        </div>
        <div className="header-actions">
          <Button variant="secondary" className="action-btn" onClick={handleRefresh}>
            <RefreshCw size={16} />
            Refresh
          </Button>
          <Button variant="secondary" className="action-btn" onClick={handlePrint}>
            <Printer size={16} />
            Print
          </Button>
          <Button variant="secondary" className="action-btn" onClick={handleExportCSV}>
            <FileDown size={16} />
            Export CSV
          </Button>
          <Button variant="primary" className="action-btn" onClick={() => setIsModalOpen(true)}>
            <Plus size={16} />
            Generate Invoices
          </Button>
        </div>
      </div>

      <div className="invoice-stats">
        <StatsCard
          title="Monthly Revenue"
          value={loadingStats ? '...' : formatCurrency(stats?.monthlyRevenue || 0)}
          trend={{ label: '0%', type: 'neutral' }}
          icon={<DollarSign size={24} />}
          color="green"
        />
        <StatsCard
          title="Outstanding"
          value={loadingStats ? '...' : formatCurrency(stats?.outstanding || 0)}
          trend={{ label: '0%', type: 'neutral' }}
          icon={<TrendingUp size={24} />}
          color="orange"
        />
        <StatsCard
          title="Paid This Month"
          value={loadingStats ? '...' : formatCurrency(stats?.paidThisMonth || 0)}
          trend={{ label: '0%', type: 'neutral' }}
          icon={<Calendar size={24} />}
          color="blue"
        />
        <StatsCard
          title="Avg Payment Time"
          value={loadingStats ? '...' : `${stats?.avgPaymentTimeDays || 0} days`}
          trend={{ label: '0%', type: 'neutral' }}
          icon={<Clock size={24} />}
          color="purple"
        />
      </div>

      <div className="invoice-content">
        <div className="invoice-management-section">
          <div className="section-header">
            <div className="section-title">
              <h2>Invoice Management</h2>
            </div>
            <div className="filters-container">
              <div className="search-container">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  className="search-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="status-filter"
              >
                <option value="All">All Status</option>
                <option value="PAID">Paid</option>
                <option value="PENDING">Pending</option>
                <option value="OVERDUE">Overdue</option>
              </select>
              <select
                value={companyFilter}
                onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
                className="company-filter"
              >
                <option value="All">All Companies</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.legalName}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Period (e.g. July 2024)"
                value={periodFilter}
                onChange={(e) => { setPeriodFilter(e.target.value); setPage(1); }}
                className="period-filter-input"
              />
            </div>
          </div>

          <div className="invoices-table">
            <table>
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  <th>Company</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>TX Count</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="empty-table">Loading invoices...</td></tr>
                ) : invoices.filter(inv =>
                  inv.invoiceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  inv.company?.legalName?.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 ? (
                  <tr><td colSpan={8} className="empty-table">No invoices found</td></tr>
                ) : (
                  invoices.filter(inv =>
                    inv.invoiceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    inv.company?.legalName?.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map((inv) => (
                    <tr key={inv.id}>
                      <td><span className="invoice-id">{inv.invoiceId}</span></td>
                      <td><span className="company-name">{inv.company?.legalName || 'N/A'}</span></td>
                      <td><span className="period-value">{inv.period}</span></td>
                      <td><span className="amount-value">{formatCurrency(inv.amount)}</span></td>
                      <td><span className="transactions-count">{inv.transactionCount}</span></td>
                      <td><span className="due-date">{formatDate(inv.dueDate)}</span></td>
                      <td>
                        <span className={`status-badge ${inv.status.toLowerCase()}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td>
                        <div className="invoice-actions">
                          <button className="download-btn"><Download size={14} /></button>
                          <InvoiceActionDropdown invoiceId={inv.id} status={inv.status} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-container">
            <div className="pagination-info">
              Showing {Math.min((page - 1) * limit + 1, total)} to {Math.min(page * limit, total)} of {total} entries
            </div>
            <div className="pagination-controls">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 1 || loading}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page * limit >= total || loading}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Generate Monthly Invoices"
        size="md"
        footer={
          <div className="modal-footer-btns">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="generate-invoice-form"
              loading={isGenerating}
            >
              Generate Invoices
            </Button>
          </div>
        }
      >
        <form id="generate-invoice-form" onSubmit={handleGenerateInvoices} className="modal-form">
          <div className="form-grid">
            <div className="form-group full-width">
              <label>
                <FileText size={16} />
                Period Label
              </label>
              <input
                type="text"
                placeholder="e.g. July 2024"
                value={genPeriod}
                onChange={(e) => setGenPeriod(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>
                <Calendar size={16} />
                Start Date
              </label>
              <input
                type="date"
                value={genStartDate}
                onChange={(e) => setGenStartDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>
                <Calendar size={16} />
                End Date
              </label>
              <input
                type="date"
                value={genEndDate}
                onChange={(e) => setGenEndDate(e.target.value)}
                required
              />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default InvoicePage;
